import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { trackWhatsAppClick } from "@/lib/trackWhatsAppClick";
import { useAuth } from "@/contexts/AuthContext";
import { NeoSDRLogo } from "@/components/layout/NeoSDRLogo";

const outsourcingNav = [
  { label: "Home", to: "/" },
  { label: "Serviços", to: "/#servicos" },
  { label: "Resultados", to: "/#resultados" },
  { label: "Blog", to: "/blog" },
];

const crmNav = [
  { label: "Home", to: "/crm-landing" },
  { label: "Recursos", to: "/crm-landing#features" },
  { label: "Preços", to: "/pricing" },
  { label: "FAQ", to: "/crm-landing#faq" },
  { label: "Blog", to: "/blog" },
  { label: "Ajuda", to: "/ajuda" },
];

interface HeaderProps {
  variant?: "outsourcing" | "crm";
}

export const Header = ({ variant = "crm" }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const navItems = variant === "crm" ? crmNav : outsourcingNav;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (to: string) => {
    setIsMobileMenuOpen(false);
    
    if (to.includes("#")) {
      const [path, hash] = to.split("#");
      const currentPath = window.location.pathname;
      
      if (path === currentPath || path === "" || (path === "/" && currentPath === "/")) {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
          return;
        }
      }
      navigate(to);
      return;
    }
    
    navigate(to);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-card/95 backdrop-blur-lg shadow-card py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <NeoSDRLogo
            className={`h-14 w-auto transition-all duration-300`}
            textColor={isScrolled ? "#182f9a" : "#ffffff"}
            iconWhite={!isScrolled}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={() => handleNavClick(item.to)}
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                isScrolled
                  ? "text-foreground/80 hover:text-primary hover:bg-primary/5"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-4">
          {variant === "crm" ? (
            <>
              <Button
                variant="ghost"
                size="default"
                className={
                  isScrolled
                    ? "text-foreground/80 hover:text-primary"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }
                asChild
              >
                <Link to={user ? "/crm" : "/admin/login"}>{user ? "Acessar CRM" : "Login"}</Link>
              </Button>
              <Button variant="hero" size="lg" asChild>
                <Link to={user ? "/crm" : "/signup"}>{user ? "Ir para o CRM" : "Testar Grátis"}</Link>
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="default"
                className={
                  isScrolled
                    ? "text-foreground/80 hover:text-primary"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }
                asChild
              >
                <Link to="/crm-landing">Conhecer CRM</Link>
              </Button>
              <Button variant="hero" size="lg" onClick={() => handleNavClick("/#diagnostico")}>
                Agendar Diagnóstico Gratuito
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className={`lg:hidden p-2 transition-colors ${
            isScrolled ? "text-foreground" : "text-white"
          }`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu — CSS transition instead of framer-motion */}
      <div
        className={`lg:hidden bg-card border-t border-border overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={() => handleNavClick(item.to)}
              className="px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary transition-colors rounded-lg hover:bg-primary/5 text-left"
            >
              {item.label}
            </button>
          ))}
          {variant === "crm" ? (
            <>
              <Button variant="outline" size="lg" className="mt-4" asChild>
                <Link to={user ? "/crm" : "/admin/login"} onClick={() => setIsMobileMenuOpen(false)}>
                  {user ? "Acessar CRM" : "Login"}
                </Link>
              </Button>
              <Button variant="hero" size="lg" className="mt-2" asChild>
                <Link to={user ? "/crm" : "/signup"} onClick={() => setIsMobileMenuOpen(false)}>
                  {user ? "Ir para o CRM" : "Testar Grátis"}
                </Link>
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="lg" className="mt-4" asChild>
                <Link to="/crm-landing" onClick={() => setIsMobileMenuOpen(false)}>
                  Conhecer CRM
                </Link>
              </Button>
              <Button variant="hero" size="lg" className="mt-2" asChild>
                <a href="https://api.whatsapp.com/send?phone=5548988653547&text=Ol%C3%A1%2C%20quero%20saber%20mais%20sobre%20a%20NeoSDR" target="_blank" rel="noopener noreferrer" onClick={() => { trackWhatsAppClick('header_mobile'); setIsMobileMenuOpen(false); }}>
                  Falar no WhatsApp
                </a>
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};
